import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import type { MachineStatusDto } from '../types/machine';
import { listMachines } from './machines.api';
import { STANDARD_LIST_BATCH_SIZE } from '../../../components/ui/list-batch-sizes';

export function useMachines(search = '', status: MachineStatusDto = 'ACTIVE', enabled = true) {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.machines(auth.activeCustomerId, search, status),
    queryFn: () => listMachines({ search, status, limit: 50 }),
    enabled: Boolean(enabled && auth.activeCustomerId),
  });
}

/** Directory only: fetch a small server-side cursor page; keep useMachines for
 * existing machine-scoped lookups which expect the original query shape. */
export function useMachineDirectory(search = '', status: MachineStatusDto = 'ACTIVE', enabled = true) {
  const auth = useAuth();
  const query = useInfiniteQuery({
    queryKey: [...queryKeys.machines(auth.activeCustomerId, search, status), 'directory'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listMachines({ search, status, cursor: pageParam, limit: STANDARD_LIST_BATCH_SIZE }),
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: Boolean(enabled && auth.activeCustomerId),
  });
  const machines = query.data?.pages.flatMap((page) => page.machines).filter((machine, index, all) =>
    all.findIndex((candidate) => candidate.id === machine.id) === index,
  );
  return {
    ...query,
    data: machines === undefined ? undefined : { machines, nextCursor: query.data?.pages.at(-1)?.nextCursor ?? null },
  };
}
